import cx from 'classnames';
import Head from 'next/head';
import NextLink from 'next/link';
import {useRouter} from 'next/router';
import pluralize from 'pluralize';

import {AnimatedText, Button, Icon, NumberTicker, Rings, TallyLogo, Text} from '#component/base';
import {contentSpacingStyles} from '#component/base/Screen/Screen.css';
import {Transition} from '#component/util';
import {sprinkles} from '#style/sprinkles';

import * as css from './Share.css';

export const Share = () => {
  const {query} = useRouter();
  let age = {
    formattedDeltaAge: {months: 0, years: 0},
    percentile: 0.0,
    isYounger: false,
  };

  try {
    age = JSON.parse(atob((query.age as string) || ''));
  } catch (e) {
    console.error('Unable to parse age', e);
  }

  const {formattedDeltaAge, isYounger} = age || {};

  return (
    <>
      <Head>
        <meta name="robots" content="noindex" key="robots" />
      </Head>
      <Transition appear className={css.root} show>
        <div className={css.header}>
          <TallyLogo color="white" size={68} />

          <Text
            as="a"
            color="white"
            href="https://www.instagram.com/tallyhealth"
            rel="noreferrer noopener"
            target="_blank"
            variant="label">
            @TallyHealth
          </Text>
        </div>
        <div className={css.content}>
          <div
            className={sprinkles({
              content: ['start', 'start', 'between'],
              dir: ['col', 'col', 'row'],
              flex: 'flex',
              items: ['start', 'start', 'center'],
            })}>
            <p className={css.headline}>
              <AnimatedText as="span" color="white" variant="heading2">
                My epigenetic age is
              </AnimatedText>
              <AnimatedText as="span" color="peach" variant="heading2">
                {isYounger ? 'younger' : 'older'}
              </AnimatedText>
              <AnimatedText as="span" color="white" variant="heading2">
                than my chronological age:
              </AnimatedText>
            </p>

            <div
              className={sprinkles({
                flex: 'flex',
                dir: 'col',
                gap: 20,
                pT: [64, 64, 0],
                pos: 'relative',
                z: 0,
              })}>
              <div
                className={sprinkles({
                  flex: 'flex',
                  placeContent: 'start',
                  placeItems: 'center',
                  pos: 'relative',
                })}>
                <Rings className={css.rings} variant={isYounger ? 'shrink' : 'grow'} />

                <Text
                  as="div"
                  className={sprinkles({flex: 'flex', placeItems: 'end', gap: 16})}
                  color="white">
                  <NumberTicker appear className={css.ageYears} color="white">
                    {`${formattedDeltaAge.years}`}
                  </NumberTicker>

                  <div className={css.ageMonths}>
                    <Icon color="peach" name={isYounger ? 'TrendDown' : 'TrendUp'} />
                    <AnimatedText color="white" variant="bodyLarge">
                      {`${pluralize('year', formattedDeltaAge.years)} ${
                        formattedDeltaAge.months > 0
                          ? `& ${pluralize('month', formattedDeltaAge.months, true)}`
                          : ''
                      }`}
                    </AnimatedText>
                  </div>
                </Text>
              </div>
            </div>
          </div>
        </div>

        <div className={cx(contentSpacingStyles, sprinkles({pB: 24}))}>
          <NextLink href="https://www.tallyhealth.com" passHref>
            <Button
              as="a"
              className={sprinkles({pos: 'relative', w: ['full', 'full', 'maxContent']})}
              variant="primary-dark">
              Discover yours at tallyhealth.com
            </Button>
          </NextLink>
        </div>
      </Transition>
    </>
  );
};
